import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { required, useGetIdentity, useRecordContext } from "ra-core";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { DateTimeInput } from "@/components/admin";

import { contactOptionText } from "../misc/ContactOption";
import {
  ACTIVE_SALES_FILTER,
  SALES_PER_PAGE,
  SALES_SORT,
  saleName,
  useCanReassignTask,
} from "./taskReassign";
import { useConfigurationContext } from "../root/ConfigurationContext";
// GAVION tasks page: the compagnie search (public.search_companies), as in the company field
import { COMPANY_SEARCH_FILTER } from "../../gavion/companies/companyCreate";

const companySearchQuery = (searchText: string) => ({
  [COMPANY_SEARCH_FILTER]: searchText,
});

export const TaskFormContent = ({
  selectContact,
  selectCompany,
  contactOptional,
}: {
  selectContact?: boolean;
  /** GAVION tasks page: pick the compagnie (required); the contact is then optional, among its contacts. */
  selectCompany?: boolean;
  /** GAVION deal tasks: the contact may be left empty (a task on the deal and its compagnie only). */
  contactOptional?: boolean;
}) => {
  const { taskTypes } = useConfigurationContext();
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        className="m-0"
        helperText={false}
      />
      {selectCompany && (
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteInput
            label="gavion.tasks.company"
            optionText="name"
            filterToQuery={companySearchQuery}
            helperText={false}
            validate={required()}
            modal
          />
        </ReferenceInput>
      )}
      {(selectContact || selectCompany) && (
        <TaskContactInput
          optional={!!(contactOptional || selectCompany)}
          byCompany={!!selectCompany}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DateTimeInput
          source="due_date"
          helperText={false}
          validate={required()}
        />
        <SelectInput
          source="type"
          validate={required()}
          choices={taskTypes}
          optionText="label"
          optionValue="value"
          defaultValue="none"
          helperText={false}
        />
      </div>
      <TaskAssigneeInput />
    </div>
  );
};

/** GAVION: « Assignée à », the active users; the current user by default. Locked for a rep on a task that is not his. */
const TaskAssigneeInput = () => {
  const { identity } = useGetIdentity();
  const record = useRecordContext();
  const canReassign = useCanReassignTask(record as never);
  return (
    <ReferenceInput
      source="sales_id"
      reference="sales"
      filter={ACTIVE_SALES_FILTER}
      sort={SALES_SORT}
      perPage={SALES_PER_PAGE}
    >
      <SelectInput
        label="gavion.tasks.assigned_to"
        optionText={saleName}
        defaultValue={identity?.id}
        disabled={!canReassign}
        validate={required()}
        helperText={false}
      />
    </ReferenceInput>
  );
};

/** The contact field; GAVION: optional on /tasks and the deal page, limited to the picked compagnie's contacts
 *  (changing the compagnie clears a contact picked for the previous one). */
const TaskContactInput = ({
  optional,
  byCompany,
}: {
  optional: boolean;
  byCompany: boolean;
}) => {
  const companyId = useWatch({ name: "company_id" });
  const { getValues, setValue } = useFormContext();
  const previousCompanyId = useRef(companyId);
  useEffect(() => {
    if (!byCompany || previousCompanyId.current === companyId) return;
    previousCompanyId.current = companyId;
    if (getValues("contact_id") != null)
      setValue("contact_id", null, { shouldDirty: true });
  }, [byCompany, companyId, getValues, setValue]);
  return (
    <ReferenceInput
      source="contact_id"
      reference="contacts_summary"
      filter={byCompany && companyId != null ? { company_id: companyId } : {}}
    >
      <AutocompleteInput
        label={
          optional
            ? "gavion.tasks.contact_optional"
            : "resources.tasks.fields.contact_id"
        }
        optionText={contactOptionText}
        helperText={false}
        validate={optional ? undefined : required()}
        modal
      />
    </ReferenceInput>
  );
};
